import { useEffect, useState } from 'react';
import MarketingMyLeads from './MarketingMyLeads';
import GuideTab from './GuideTab';
import { BACKEND_CONFIG } from '../../utils/productionConfig';
import { axiosInstance } from '../../lib/api-client';
import { toast } from 'sonner';
import { Check, CheckCircle, Clock, Copy, DollarSign, Loader2, Percent, Plus, TrendingUp, Wallet, XCircle, Gift } from 'lucide-react';
import { PayoutSection } from './PayoutSection';
import { PromotionsManagement } from '../marketing-manager/PromotionsManagement';
const API_URL = BACKEND_CONFIG.apiUrl;

interface Coupon {
  _id: string;
  code: string;
  discountPercentage: number;  // FIXED: was "discount"
  planId: any; // Can be string or populated object
  planName?: string;
  isActive: boolean;
  usedCount: number;  // FIXED: was "usageCount"
  maxUses: number;  // ADDED
  description?: string;  // ADDED
  validUntil: string;  // FIXED: was "expiresAt"
  createdAt: string;
}

interface Transaction {
  _id: string;
  couponCode: string;
  buyerName: string;
  buyerEmail: string;
  planName: string;
  originalAmount: number;
  discountAmount: number;
  paidAmount: number;
  commissionAmount: number;
  commissionRate: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  approvedAt?: string;
  approvedBy?: string;
}

interface Plan {
  _id: string;
  name: string;
  price: number;
}

interface MarketingStats {
  employmentType?: 'commission' | 'salary';
  totalEarnings: number;
  pendingEarnings: number;
  totalSalesCount?: number;
  totalRevenueGenerated?: number;
  walletBalance: number;
  totalCouponsUsed: number;
  activeCoupons: number;
}

interface MarketingPanelProps {
  availablePlans?: Array<{ _id: string; name: string; price: number }>;
  onClose?: () => void;
}

export function MarketingPanel({ availablePlans = [], onClose }: MarketingPanelProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'coupons' | 'transactions' | 'leads' | 'guide' | 'promotions'>('overview');
  const [showCreateCoupon, setShowCreateCoupon] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [canCreatePromotions, setCanCreatePromotions] = useState(false);
  const [showPromotionsTab, setShowPromotionsTab] = useState(true);

  // Form state
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(10);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [expiryDays, setExpiryDays] = useState(30);
  const [maxUsageLimit, setMaxUsageLimit] = useState(0); // NEW: Usage limit
  const [description, setDescription] = useState(''); // NEW: Description

  // Data state
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [stats, setStats] = useState<MarketingStats>({
    totalEarnings: 0,
    pendingEarnings: 0,
    walletBalance: 0,
    totalCouponsUsed: 0,
    activeCoupons: 0
  });
  const [commissionRate, setCommissionRate] = useState(50);
  const [showMyLeadsTab, setShowMyLeadsTab] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);
  
  // Periodic check for settings changes
  useEffect(() => {
    // Refresh settings every 30 seconds to catch admin changes
    const settingsInterval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Check lead settings
        const leadSettingsRes = await axiosInstance.get(`${API_URL}/leads/admin/settings`).catch(() => ({ data: { success: false } }));
        
        if (leadSettingsRes.data.success) {
          const showLeadsTab = leadSettingsRes.data.data?.showMyLeadsTab !== false;
          console.log('🔧 Lead Settings Response:', {
            success: leadSettingsRes.data.success,
            showMyLeadsTab: leadSettingsRes.data.data?.showMyLeadsTab,
            computed: showLeadsTab
          });
          setShowMyLeadsTab(showLeadsTab);
        } else {
          console.log('⚠️ Lead settings fetch failed, defaulting to true');
          setShowMyLeadsTab(true);
        }
        
        // Check promotional settings
        const promotionSettingsRes = await axiosInstance.get(`${API_URL}/promotional-orders/settings`).catch(() => ({ data: { success: false } }));
        
        if (promotionSettingsRes.data.success) {
          const data = promotionSettingsRes.data.data;
          const canCreate = data.canCreatePromotionalOrders || false;
          const showTab = data.showPromotionsTab !== false;
          
          console.log('🔧 Promotion Settings Response:', {
            success: promotionSettingsRes.data.success,
            canCreatePromotionalOrders: data.canCreatePromotionalOrders,
            showPromotionsTab: data.showPromotionsTab,
            computedCanCreate: canCreate,
            computedShowTab: showTab
          });
          
          setCanCreatePromotions(canCreate);
          setShowPromotionsTab(showTab);
        } else {
          console.log('⚠️ Promotion settings fetch failed, defaulting to false for permissions');
          setCanCreatePromotions(false);
          setShowPromotionsTab(true);
        }
      } catch (error) {
        console.error('Error refreshing settings:', error);
      }
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(settingsInterval);
  }, []);

  // Reset activeTab if My Leads tab is hidden
  useEffect(() => {
    if (!showMyLeadsTab && activeTab === 'leads') {
      console.log('🔄 My Leads tab hidden, switching to overview');
      setActiveTab('overview');
    }
    if (!showPromotionsTab && activeTab === 'promotions') {
      console.log('🔄 Promotions tab hidden, switching to overview');
      setActiveTab('overview');
    }
  }, [showMyLeadsTab, showPromotionsTab, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to access marketing panel');
        return;
      }

      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Fetch all data in parallel
      const [couponsRes, transactionsRes, plansRes, statsRes, settingsRes, leadSettingsRes, promotionSettingsRes] = await Promise.all([
        axiosInstance.get(`${API_URL}/marketing/coupons`).catch(() => ({ data: { success: false, data: [] } })),
        axiosInstance.get(`${API_URL}/marketing/transactions`).catch(() => ({ data: { success: false, data: [] } })),
        axiosInstance.get(`${API_URL}/subscriptions/plans`).catch(() => ({ data: { success: false, data: [] } })),
        axiosInstance.get(`${API_URL}/marketing/stats`).catch(() => ({ data: { success: false, data: {} } })),
        axiosInstance.get(`${API_URL}/marketing/commissions/settings`).catch(() => ({ data: { success: false, data: {} } })),
        axiosInstance.get(`${API_URL}/leads/admin/settings`).catch(() => ({ data: { success: false } })),
        axiosInstance.get(`${API_URL}/promotional-orders/settings`).catch(() => ({ data: { success: false } }))
      ]);

      if (couponsRes.data.success) {
        setCoupons(couponsRes.data.data?.coupons || []);
      } else {
        setCoupons([]);
      }

      if (transactionsRes.data.success) {
        setTransactions(transactionsRes.data.data || []);
      } else {
        setTransactions([]);
      }

      if (plansRes.data.success) {
        setPlans(plansRes.data.data || []);
      }

      if (statsRes.data.success) {
        const statsData = statsRes.data.data?.stats || {};
        setStats({
          totalEarnings: statsData.totalEarnings || 0,
          pendingEarnings: statsData.pendingEarnings || 0,
          walletBalance: statsData.withdrawnEarnings || 0,
          totalCouponsUsed: statsData.totalOrders || 0,
          activeCoupons: statsData.activeCoupons || 0
        });
      }

      if (settingsRes.data.success) {
        setCommissionRate(settingsRes.data.data?.commissionRate || 50);
      }

      if (leadSettingsRes.data.success) {
        const showLeadsTab = leadSettingsRes.data.data?.showMyLeadsTab !== false;
        console.log('🔧 Lead Settings Response:', {
          success: leadSettingsRes.data.success,
          showMyLeadsTab: leadSettingsRes.data.data?.showMyLeadsTab,
          computed: showLeadsTab
        });
        setShowMyLeadsTab(showLeadsTab);
      } else {
        console.log('⚠️ Lead settings fetch failed, defaulting to true');
        setShowMyLeadsTab(true);
      }

      if (promotionSettingsRes.data.success) {
        const data = promotionSettingsRes.data.data;
        const canCreate = data.canCreatePromotionalOrders || false;
        const showTab = data.showPromotionsTab !== false;
        
        console.log('🔧 Promotion Settings Response:', {
          success: promotionSettingsRes.data.success,
          canCreatePromotionalOrders: data.canCreatePromotionalOrders,
          showPromotionsTab: data.showPromotionsTab,
          computedCanCreate: canCreate,
          computedShowTab: showTab
        });
        
        setCanCreatePromotions(canCreate);
        setShowPromotionsTab(showTab);
      } else {
        console.log('⚠️ Promotion settings fetch failed, defaulting to false for permissions');
        setCanCreatePromotions(false);
        setShowPromotionsTab(true);
      }

    } catch (error: any) {
      console.error('Error fetching marketing data:', error);
      toast.error('Failed to load marketing data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async () => {
    if (!couponCode || !selectedPlan) {
      toast.error('Please fill all required fields');
      return;
    }

    if (couponCode.length < 4) {
      toast.error('Coupon code must be at least 4 characters');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Calculate validUntil date from expiryDays
      const validUntilDate = new Date();
      validUntilDate.setDate(validUntilDate.getDate() + expiryDays);

      const response = await axiosInstance.post(`${API_URL}/marketing/coupons`, {
        code: couponCode.toUpperCase(),
        planId: selectedPlan,
        discountPercentage: discount,
        maxUses: maxUsageLimit, // NEW: Usage limit
        validUntil: validUntilDate.toISOString(),
        description: description // NEW: Description
      });

      if (response.data.success) {
        toast.success('Coupon created successfully!');
        setCoupons([response.data.data, ...coupons]);
        setShowCreateCoupon(false);
        setCouponCode('');
        setDiscount(10);
        setSelectedPlan('');
        setExpiryDays(30);
        setMaxUsageLimit(0); // NEW: Usage limit
        setDescription(''); // NEW: Description
        
        // Refresh stats
        fetchData();
      }
    } catch (error: any) {
      console.error('Error creating coupon:', error);
      toast.error(error.response?.data?.message || 'Failed to create coupon');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleCoupon = async (couponId: string) => {
    try {
      const token = localStorage.getItem('token');
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axiosInstance.put(`${API_URL}/marketing/coupons/${couponId}/toggle`);

      if (response.data.success) {
        setCoupons(coupons.map(c => 
          c._id === couponId ? response.data.data : c
        ));
        toast.success(`Coupon ${response.data.data.isActive ? 'activated' : 'deactivated'} successfully`);
      }
    } catch (error: any) {
      console.error('Error toggling coupon:', error);
      toast.error(error.response?.data?.message || 'Failed to update coupon');
    }
  };

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Coupon code copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatCurrency = (amount: number) => {
    // Handle undefined, null, or NaN values
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '₹0';
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPlanName = (coupon: Coupon) => {
    // Handle both populated and unpopulated planId
    if (coupon.planName) {
      return coupon.planName;
    }
    if (typeof coupon.planId === 'object' && coupon.planId?.name) {
      return coupon.planId.name;
    }
    return 'Unknown Plan';
  };

  if (loading && coupons.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-pink-600 mx-auto mb-3" />
          <p className="text-gray-600">Loading marketing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-6">
      <div className="max-w-7xl mx-auto">
        {/* Stats Cards - More Compact */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-3 sm:p-4 text-white">
            <div className="flex items-center justify-between mb-2">
              <Wallet className="w-5 h-5 sm:w-6 sm:h-6 opacity-80" />
              <span className="text-xs opacity-80">Approved</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold mb-0.5">{formatCurrency(stats.totalEarnings)}</div>
            <p className="text-xs opacity-80">Total Earnings</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-3 sm:p-4 text-white">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 opacity-80" />
              <span className="text-xs opacity-80">Pending</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold mb-0.5">{formatCurrency(stats.pendingEarnings)}</div>
            <p className="text-xs opacity-80">Awaiting Approval</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-3 sm:p-4 text-white">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 opacity-80" />
              <span className="text-xs opacity-80">Total</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold mb-0.5">{stats.totalCouponsUsed}</div>
            <p className="text-xs opacity-80">Coupons Used</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-3 sm:p-4 text-white">
            <div className="flex items-center justify-between mb-2">
              <Percent className="w-5 h-5 sm:w-6 sm:h-6 opacity-80" />
              <span className="text-xs opacity-80">Active</span>
            </div>
            <div className="text-xl sm:text-2xl font-bold mb-0.5">{stats.activeCoupons}</div>
            <p className="text-xs opacity-80">Active Coupons</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="border-b border-gray-200 overflow-x-auto">
            <div className="flex space-x-4 sm:space-x-8 px-4 sm:px-6 min-w-max">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-3 sm:py-4 border-b-2 transition-colors text-sm sm:text-base whitespace-nowrap ${
                  activeTab === 'overview'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('coupons')}
                className={`py-3 sm:py-4 border-b-2 transition-colors text-sm sm:text-base whitespace-nowrap ${
                  activeTab === 'coupons'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                My Coupons ({coupons.length})
              </button>
              <button
                onClick={() => setActiveTab('transactions')}
                className={`py-3 sm:py-4 border-b-2 transition-colors text-sm sm:text-base whitespace-nowrap ${
                  activeTab === 'transactions'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Transactions ({transactions.length})
              </button>
              {showMyLeadsTab && (
                <button
                  onClick={() => setActiveTab('leads')}
                  className={`py-3 sm:py-4 border-b-2 transition-colors text-sm sm:text-base whitespace-nowrap ${
                    activeTab === 'leads'
                      ? 'border-pink-500 text-pink-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  My Leads
                </button>
              )}
              <button
                onClick={() => setActiveTab('guide')}
                className={`hidden py-3 sm:py-4 border-b-2 transition-colors text-sm sm:text-base whitespace-nowrap ${
                  activeTab === 'guide'
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Guide
              </button>
              {showPromotionsTab && (
                <button
                  onClick={() => setActiveTab('promotions')}
                  className={`py-3 sm:py-4 border-b-2 transition-colors text-sm sm:text-base whitespace-nowrap ${
                    activeTab === 'promotions'
                      ? 'border-pink-500 text-pink-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Promotions
                </button>
              )}
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <h3 className="text-base sm:text-lg text-gray-900 mb-3 sm:mb-4">Commission Structure</h3>
                  <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 sm:p-6 border border-pink-200">
                    <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div className="bg-pink-100 rounded-full p-2 sm:p-3">
                        <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-pink-600" />
                      </div>
                      <div>
                        <div className="text-2xl sm:text-3xl font-bold text-gray-900">{commissionRate}%</div>
                        <p className="text-sm sm:text-base text-gray-600">Commission Rate</p>
                      </div>
                    </div>
                    <p className="text-sm sm:text-base text-gray-700">
                      You earn {commissionRate}% commission on every successful payment made using your coupons.
                      Commissions are paid after admin approval.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-base sm:text-lg text-gray-900 mb-3 sm:mb-4">How It Works</h3>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-pink-500 text-white rounded-full flex items-center justify-center text-sm sm:text-base">1</div>
                      <div>
                        <div className="text-sm sm:text-base font-medium text-gray-900">Create a Coupon</div>
                        <p className="text-xs sm:text-sm text-gray-600">Set discount percentage and select which plan it applies to</p>
                      </div>
                    </div>
                    <div className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-pink-500 text-white rounded-full flex items-center justify-center text-sm sm:text-base">2</div>
                      <div>
                        <div className="text-sm sm:text-base font-medium text-gray-900">Share with Users</div>
                        <p className="text-xs sm:text-sm text-gray-600">Users apply your coupon code during payment</p>
                      </div>
                    </div>
                    <div className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-pink-500 text-white rounded-full flex items-center justify-center text-sm sm:text-base">3</div>
                      <div>
                        <div className="text-sm sm:text-base font-medium text-gray-900">Earn Commission</div>
                        <p className="text-xs sm:text-sm text-gray-600">Get {commissionRate}% of the payment amount (after discount)</p>
                      </div>
                    </div>
                    <div className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-pink-500 text-white rounded-full flex items-center justify-center text-sm sm:text-base">4</div>
                      <div>
                        <div className="text-sm sm:text-base font-medium text-gray-900">Admin Approval</div>
                        <p className="text-xs sm:text-sm text-gray-600">Admin approves and credits amount to your wallet</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-base sm:text-lg text-gray-900 mb-3 sm:mb-4">Example Calculation</h3>
                  <div className="bg-blue-50 rounded-lg p-4 sm:p-6 border border-blue-200">
                    <div className="space-y-2 text-xs sm:text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Plan Original Price:</span>
                        <span className="font-medium text-gray-900">₹1,000</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Your Coupon Discount (20%):</span>
                        <span className="font-medium text-red-600">-₹200</span>
                      </div>
                      <div className="flex justify-between border-t border-blue-300 pt-2">
                        <span className="text-gray-700">User Pays:</span>
                        <span className="font-medium text-gray-900">₹800</span>
                      </div>
                      <div className="flex justify-between border-t border-blue-300 pt-2">
                        <span className="text-gray-700">Your Commission ({commissionRate}% of ₹800):</span>
                        <span className="text-base sm:text-lg font-bold text-green-600">₹{(800 * commissionRate / 100).toFixed(0)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Coupons Tab */}
            {activeTab === 'coupons' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-6">
                  <div>
                    <h3 className="text-base sm:text-lg text-gray-900">Your Coupons</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Create and manage discount coupons for plans</p>
                  </div>
                  <button
                    onClick={() => setShowCreateCoupon(!showCreateCoupon)}
                    className="flex items-center justify-center gap-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:shadow-lg transition-all text-sm sm:text-base"
                  >
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                    Create Coupon
                  </button>
                </div>

                {/* Create Coupon Form */}
                {showCreateCoupon && (
                  <div className="bg-gray-50 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 border-2 border-pink-200">
                    <h4 className="text-base sm:text-lg text-gray-900 mb-3 sm:mb-4">Create New Coupon</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                      <div>
                        <label className="block text-xs sm:text-sm text-gray-700 mb-2">Coupon Code *</label>
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          placeholder="e.g., SAVE20"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm text-gray-700 mb-2">Discount Percentage *</label>
                        <select
                          value={discount}
                          onChange={(e) => setDiscount(Number(e.target.value))}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm sm:text-base"
                        >
                          <option value={5}>5%</option>
                          <option value={10}>10%</option>
                          <option value={15}>15%</option>
                          <option value={20}>20%</option>
                          <option value={25}>25%</option>
                          <option value={30}>30%</option>
                          <option value={40}>40%</option>
                          <option value={50}>50%</option>
                          <option value={60}>60%</option>
                          <option value={70}>70%</option>
                          <option value={80}>80%</option>
                          <option value={90}>90%</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm text-gray-700 mb-2">Applicable Plan *</label>
                        <select
                          value={selectedPlan}
                          onChange={(e) => setSelectedPlan(e.target.value)}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm sm:text-base"
                        >
                          <option value="">Select a plan</option>
                          {plans.map(plan => (
                            <option key={plan._id} value={plan._id}>
                              {plan.name} - {formatCurrency(plan.price)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm text-gray-700 mb-2">Valid For (Days)</label>
                        <input
                          type="number"
                          value={expiryDays}
                          onChange={(e) => setExpiryDays(Number(e.target.value))}
                          min="1"
                          max="365"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm text-gray-700 mb-2">Usage Limit (0 for unlimited)</label>
                        <input
                          type="number"
                          value={maxUsageLimit}
                          onChange={(e) => setMaxUsageLimit(Number(e.target.value))}
                          min="0"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm text-gray-700 mb-2">Description (optional)</label>
                        <input
                          type="text"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="e.g., Limited time offer"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm sm:text-base"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 sm:gap-3">
                      <button
                        onClick={handleCreateCoupon}
                        disabled={creating}
                        className="flex-1 sm:flex-initial bg-pink-500 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 text-sm sm:text-base"
                      >
                        {creating ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                            Creating...
                          </>
                        ) : (
                          'Create Coupon'
                        )}
                      </button>
                      <button
                        onClick={() => setShowCreateCoupon(false)}
                        className="flex-1 sm:flex-initial bg-gray-200 text-gray-700 px-4 sm:px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm sm:text-base"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Coupons List */}
                <div className="space-y-3 sm:space-y-4">
                  {coupons.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                      <Percent className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                      <p className="text-sm sm:text-base text-gray-600">No coupons created yet</p>
                      <p className="text-xs sm:text-sm text-gray-500">Create your first coupon to start earning</p>
                    </div>
                  ) : (
                    coupons.map(coupon => (
                      <div key={coupon._id} className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                              <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-lg font-mono font-bold text-base sm:text-lg">
                                {coupon.code}
                              </div>
                              <button
                                onClick={() => copyToClipboard(coupon.code)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Copy code"
                              >
                                {copiedCode === coupon.code ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                  <Copy className="w-4 h-4 text-gray-600" />
                                )}
                              </button>
                              <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${
                                coupon.isActive 
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {coupon.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm sm:text-base text-gray-900 font-medium">{getPlanName(coupon)}</p>
                              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  <Percent className="w-3 h-3 sm:w-4 sm:h-4" />
                                  {coupon.discountPercentage}% Discount
                                </span>
                                <span>Used {coupon.usedCount} times</span>
                                {coupon.validUntil && (
                                  <span className="text-xs">Expires: {formatDate(coupon.validUntil)}</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">Created: {formatDate(coupon.createdAt)}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleToggleCoupon(coupon._id)}
                            className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors w-full sm:w-auto ${
                              coupon.isActive
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            {coupon.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div>
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-base sm:text-lg text-gray-900">Transaction History</h3>
                  <p className="text-xs sm:text-sm text-gray-600">Track all earnings from coupon usage</p>
                </div>

                {transactions.length === 0 ? (
                  <div className="text-center py-8 sm:py-12">
                    <DollarSign className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                    <p className="text-sm sm:text-base text-gray-600">No transactions yet</p>
                    <p className="text-xs sm:text-sm text-gray-500">Share your coupons to start earning</p>
                  </div>
                ) : (
                  <div className="space-y-3 sm:hidden">
                    {/* Mobile Card View */}
                    {transactions.map(transaction => (
                      <div key={transaction._id} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="font-medium text-gray-900 text-sm">{transaction.buyerName}</div>
                            <div className="text-xs text-gray-500">{transaction.buyerEmail}</div>
                          </div>
                          {transaction.status === 'approved' && (
                            <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                              <CheckCircle className="w-3 h-3" />
                              Approved
                            </div>
                          )}
                          {transaction.status === 'pending' && (
                            <div className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
                              <Clock className="w-3 h-3" />
                              Pending
                            </div>
                          )}
                          {transaction.status === 'rejected' && (
                            <div className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium">
                              <XCircle className="w-3 h-3" />
                              Rejected
                            </div>
                          )}
                        </div>
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Coupon:</span>
                            <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-mono font-medium">
                              {transaction.couponCode}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Plan:</span>
                            <span className="text-gray-900">{transaction.planName}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Original:</span>
                            <span className="text-gray-900">{formatCurrency(transaction.originalAmount)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Paid:</span>
                            <span className="font-medium text-gray-900">{formatCurrency(transaction.paidAmount)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2">
                            <span className="text-gray-600">Commission:</span>
                            <div className="text-right">
                              <div className="font-bold text-green-600">{formatCurrency(transaction.commissionAmount)}</div>
                              <div className="text-xs text-gray-500">({transaction.commissionRate}%)</div>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 pt-1">
                            {formatDate(transaction.createdAt)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Desktop Table View */}
                {transactions.length > 0 && (
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-sm text-gray-700">Date</th>
                          <th className="text-left py-3 px-4 text-sm text-gray-700">User</th>
                          <th className="text-left py-3 px-4 text-sm text-gray-700">Coupon</th>
                          <th className="text-left py-3 px-4 text-sm text-gray-700">Plan</th>
                          <th className="text-right py-3 px-4 text-sm text-gray-700">Original</th>
                          <th className="text-right py-3 px-4 text-sm text-gray-700">Paid</th>
                          <th className="text-right py-3 px-4 text-sm text-gray-700">Commission</th>
                          <th className="text-center py-3 px-4 text-sm text-gray-700">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map(transaction => (
                          <tr key={transaction._id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-4 px-4 text-sm text-gray-600">
                              {formatDate(transaction.createdAt)}
                            </td>
                            <td className="py-4 px-4">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{transaction.buyerName}</div>
                                <div className="text-xs text-gray-500">{transaction.buyerEmail}</div>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-mono font-medium">
                                {transaction.couponCode}
                              </span>
                            </td>
                            <td className="py-4 px-4 text-sm text-gray-900">{transaction.planName}</td>
                            <td className="py-4 px-4 text-right text-sm text-gray-600">
                              {formatCurrency(transaction.originalAmount)}
                            </td>
                            <td className="py-4 px-4 text-right text-sm font-medium text-gray-900">
                              {formatCurrency(transaction.paidAmount)}
                            </td>
                            <td className="py-4 px-4 text-right">
                              <div className="text-sm font-bold text-green-600">
                                {formatCurrency(transaction.commissionAmount)}
                              </div>
                              <div className="text-xs text-gray-500">
                                ({transaction.commissionRate}%)
                              </div>
                            </td>
                            <td className="py-4 px-4 text-center">
                              {transaction.status === 'approved' && (
                                <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                                  <CheckCircle className="w-3 h-3" />
                                  Approved
                                </div>
                              )}
                              {transaction.status === 'pending' && (
                                <div className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-medium">
                                  <Clock className="w-3 h-3" />
                                  Pending
                                </div>
                              )}
                              {transaction.status === 'rejected' && (
                                <div className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-medium">
                                  <XCircle className="w-3 h-3" />
                                  Rejected
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Leads Tab */}
            {activeTab === 'leads' && (
              <div>
                <MarketingMyLeads />
              </div>
            )}

            {/* Guide Tab */}
            {activeTab === 'guide' && (
              <GuideTab employmentType={stats.employmentType || 'commission'} />
            )}

            {/* Promotions Tab */}
            {activeTab === 'promotions' && (
              <div>
                <PromotionsManagement />
              </div>
            )}
          </div>
        </div>

        {/* Payout Section */}
        <PayoutSection 
          walletBalance={stats.walletBalance}
          pendingEarnings={stats.pendingEarnings}
          totalEarnings={stats.totalEarnings}
          onRefresh={fetchData}
        />
      </div>
    </div>
  );
}